export interface SelectPopoverHeightOptions {
  /** Number of items rendered in the list. */
  itemCount: number;
  /** When true, reserve space for a loading spinner instead of the list. */
  isLoading?: boolean;
  /**
   * Fixed height (px) to always add, e.g. a persistent "None" row that sits
   * above the scrollable list.
   */
  baseExtraHeight?: number;
  /**
   * When provided, reserve this height for a message (e.g. "type 3+
   * characters") instead of the item/empty height.
   */
  messageHeight?: number;
  /** Maximum popover height (px). */
  maxHeight?: number;
  /** Height (px) of a single list item. */
  itemHeight?: number;
  /** Height (px) of the search input. */
  inputHeight?: number;
}

const PADDING_AND_BORDERS = 10;
const LOADING_SPINNER_HEIGHT = 68;
const COMMAND_EMPTY_HEIGHT = 68;

/**
 * Computes a popover height that adapts to the number of items in a searchable
 * select, so short lists render compactly instead of leaving empty space. The
 * result is capped at `maxHeight`, beyond which the list scrolls.
 */
export function calculateSelectPopoverHeight({
  itemCount,
  isLoading = false,
  baseExtraHeight = 0,
  messageHeight,
  maxHeight = 300,
  itemHeight = 32,
  inputHeight = 40,
}: SelectPopoverHeightOptions): number {
  let contentHeight = inputHeight + PADDING_AND_BORDERS + baseExtraHeight;

  if (isLoading) {
    contentHeight += LOADING_SPINNER_HEIGHT;
  } else if (messageHeight != null) {
    contentHeight += messageHeight;
  } else if (itemCount === 0) {
    contentHeight += COMMAND_EMPTY_HEIGHT;
  } else {
    contentHeight += itemCount * itemHeight;
  }

  return Math.min(contentHeight, maxHeight);
}
